import { useState } from "react";
import { KeyRound, Search } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime, money } from "@/lib/api";
import { DemoBadge, ErrorBox, Field, PageHead, Spinner, StatusBadge, useFetch } from "@/components/common";
import { CredentialDialog } from "@/components/CredentialDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

function CustomerDetail({ uid, onClose }) {
  const { data: u } = useFetch(`/admin/customers/${uid}`);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl" data-testid="customer-detail-dialog">
        <DialogHeader><DialogTitle>{u?.full_name || "Customer"}</DialogTitle><DialogDescription className="font-mono">{u?.username}</DialogDescription></DialogHeader>
        {!u ? <Spinner /> : (
          <div className="space-y-5 text-sm">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <p>Mobile: <b>{u.mobile}</b></p><p>Birthdate: <b>{u.birthdate || "—"}</b></p>
              <p>Consent: <b>{u.consent ? fmtDateTime(u.consent.accepted_at) : "Not given"}</b></p><p>Family members: <b>{u.beneficiaries?.length || 0}</b></p>
            </div>
            {u.packages.map((cp) => (
              <div key={cp.id} className="rounded-2xl border border-slate-100 p-4">
                <div className="flex items-center justify-between"><p className="font-bold">{cp.package_name}</p><StatusBadge status={cp.status} /></div>
                <p className="text-xs text-slate-500">Card {cp.card_serial} · Activated {fmtDate(cp.activated_at)} · Expires {fmtDate(cp.expires_at)}</p>
              </div>
            ))}
            {u.entitlements.length > 0 && (
              <table className="data-table"><thead><tr><th>Benefit</th><th>Used</th><th>Remaining</th><th /></tr></thead>
                <tbody>{u.entitlements.map((e) => <tr key={e.id}><td>{e.benefit_name}</td><td>{e.used_qty}</td><td>{e.remaining_qty ?? "∞"}</td><td>{e.is_demo && <DemoBadge />}</td></tr>)}</tbody></table>
            )}
            <div><p className="label">Payments</p>{u.payments.map((p) => <p key={p.id} className="text-xs">{p.reference} · {money(p.amount)} · {p.status} {p.mode === "DEMO" && <DemoBadge />}</p>)}</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Customers() {
  const [q, setQ] = useState("");
  const { data, error, loading, reload } = useFetch(`/admin/customers${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  const [creds, setCreds] = useState(null);
  const [viewing, setViewing] = useState(null);
  const reset = async (u) => {
    if (!window.confirm(`Reset password for ${u.username}? Their current sessions will be signed out.`)) return;
    try { const { data: d } = await api.post(`/admin/customers/${u.id}/reset-password`); setCreds(d); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (u) => {
    try { await api.patch(`/admin/users/${u.id}/active`, { active: !u.active }); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHead eyebrow="Customers" title="Customer accounts" sub="Customers self-register after cashier activation. The physical card serial is the username." />
      <div className="relative mb-4 max-w-sm"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input className="field pl-9" placeholder="Search serial, name or mobile" value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search-input" /></div>
      <ErrorBox error={error} />
      {loading && !data ? <Spinner /> : (
        <div className="surface overflow-x-auto">
          <table className="data-table" data-testid="customers-table">
            <thead><tr><th>Username / Card</th><th>Name</th><th>Mobile</th><th>Package</th><th>Account</th><th /></tr></thead>
            <tbody>{data?.map((u) => (
              <tr key={u.id} data-testid={`customer-row-${u.username}`}>
                <td className="font-mono font-semibold">{u.username}</td>
                <td><button className="font-semibold text-purple-700 hover:underline" onClick={() => setViewing(u.id)} data-testid={`view-customer-${u.username}`}>{u.full_name}</button></td>
                <td>{u.mobile}</td>
                <td>{u.packages.map((p) => <StatusBadge key={p.id} status={p.status} />)}</td>
                <td>{u.must_change_password ? <StatusBadge status="PENDING" testId="pw-change-pending" /> : <StatusBadge status={u.active ? "ACTIVE" : "INACTIVE"} />}</td>
                <td className="whitespace-nowrap text-right">
                  <button className="btn-ghost btn-sm" onClick={() => reset(u)} data-testid={`reset-password-${u.username}`}><KeyRound size={13} /> Reset</button>
                  <button className="btn-ghost btn-sm" onClick={() => toggle(u)} data-testid={`toggle-active-${u.username}`}>{u.active ? "Disable" : "Enable"}</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <CredentialDialog creds={creds} onClose={() => setCreds(null)} />
      {viewing && <CustomerDetail uid={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
